import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { getIcon } from '../utils/getIcon';
const BASE_BOTTOM = 32;
// MarketDetails renders a fixed action bar at the bottom on mobile only
// (md:hidden), so the mobile button sits higher there. Desktop keeps BASE_BOTTOM.
const MARKET_DETAILS_MOBILE_BASE_BOTTOM = 80;
const FOOTER_PADDING = 16;
export const SwapButton = ({ isSwapModalOpen, onClick, themeName, isTokenActionModalOpen = false, }) => {
    const mobileWrapperRef = useRef(null);
    const desktopWrapperRef = useRef(null);
    const rafRef = useRef(null);
    const { pathname } = useLocation();
    const mobileBaseBottom = pathname.startsWith('/market/')
        ? MARKET_DETAILS_MOBILE_BASE_BOTTOM
        : BASE_BOTTOM;
    useLayoutEffect(() => {
        if (isSwapModalOpen || isTokenActionModalOpen)
            return;
        const updatePosition = () => {
            const footer = document.querySelector('footer');
            if (!footer)
                return;
            const footerRect = footer.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            // Both wrappers ride above the footer once it is in view, but never below
            // their own resting offset — the offsets are floors, not alternatives.
            // With the footer off-screen footerRect.top > viewportHeight, so
            // footerBottom always loses to the base.
            const footerBottom = Math.round(viewportHeight - footerRect.top + FOOTER_PADDING);
            if (mobileWrapperRef.current) {
                mobileWrapperRef.current.style.bottom = `${Math.max(mobileBaseBottom, footerBottom)}px`;
            }
            if (desktopWrapperRef.current) {
                desktopWrapperRef.current.style.bottom = `${Math.max(BASE_BOTTOM, footerBottom)}px`;
            }
        };
        const handleScroll = () => {
            if (rafRef.current)
                cancelAnimationFrame(rafRef.current);
            rafRef.current = requestAnimationFrame(updatePosition);
        };
        const timeoutId = setTimeout(updatePosition, 0);
        window.addEventListener('scroll', handleScroll, { passive: true });
        window.addEventListener('resize', handleScroll, { passive: true });
        return () => {
            clearTimeout(timeoutId);
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleScroll);
            if (rafRef.current)
                cancelAnimationFrame(rafRef.current);
        };
    }, [isSwapModalOpen, isTokenActionModalOpen, mobileBaseBottom]);
    if (isSwapModalOpen)
        return null;
    // The wrapper mirrors the page layout container (max-w-[1390px] mx-auto)
    // so the button naturally sits at the right edge of the content column —
    // same technique used by NotificationComp. Only `bottom` is set via JS.
    const mobileWrapperStyle = {
        position: 'fixed',
        bottom: `${mobileBaseBottom}px`,
        left: 0,
        right: 0,
        pointerEvents: 'none',
        zIndex: 39,
    };
    const desktopWrapperStyle = {
        position: 'fixed',
        bottom: `${BASE_BOTTOM}px`,
        left: 0,
        right: 0,
        pointerEvents: 'none',
        zIndex: 50,
    };
    // Pull the button into the gutter only when viewport > 1920px.
    // Cap the offset at the 1920px value so it never goes further out
    // regardless of how wide the screen is.
    const gutterOffset = window.innerWidth >= 1920 ? { marginRight: '-145px' } : {};
    return (_jsxs(_Fragment, { children: [!isTokenActionModalOpen && (_jsx("div", { ref: mobileWrapperRef, style: mobileWrapperStyle, className: "flex md:hidden", children: _jsx("div", { className: "w-full max-w-[1390px] mx-auto px-3 flex justify-end", children: _jsx("button", { onClick: onClick, style: { pointerEvents: 'auto' }, type: "button", "aria-label": "Open Swap Modal", children: _jsx("img", { src: themeName === 'dark' ? getIcon('whiteSwapButton') : getIcon('blackSwapButton'), className: "w-[50px] h-[50px]", alt: "Swap Button" }) }) }) })), _jsx("div", { ref: desktopWrapperRef, style: desktopWrapperStyle, className: "hidden md:flex", children: _jsx("div", { className: "w-full max-w-[1390px] mx-auto flex justify-end px-4 xl:px-[unset]", children: _jsx("button", { onClick: onClick, style: Object.assign({ pointerEvents: 'auto' }, gutterOffset), type: "button", "aria-label": "Open Swap Modal", children: _jsxs("div", { className: "flex items-center justify-center theme-bg-swap-button md:rounded-[9px] lg:rounded-[11px] xl:rounded-[15px] py-2 px-3 xl:!py-3.5 xl:!px-4", children: [_jsx("img", { src: themeName === 'dark'
                                        ? getIcon('swapDesktopBlackIcon')
                                        : getIcon('swapDesktopWhiteIcon'), className: "md:w-[14px] md:h-[14px] lg:w-[17px] lg:h-[17px] xl:w-[25px] xl:h-[25px] mr-1", alt: "Swap Button" }), _jsx("span", { className: "md:text-sm lg:text-base xl:text-xl font-InterMedium theme-text-swap-btn-desktop", children: "Swap" })] }) }) }) })] }));
};
